/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { addClasses, cn } from 'lib/helpers/class.js';
import Button from '#lib/components/button.jsx';
import Input from '#lib/components/input.jsx';
import { toast } from 'react-hot-toast';
import Dialog from '#lib/components/dialog.jsx';
import { Icon } from '@iconify-icon/react';
import HTMLEditor from './html-editor.jsx';
import { validateStringWithRegex } from '#lib/helpers/regex.js';
import { useSendEmail } from '#hooks/use-send-email.js';

export function SharePanel(props) {
    const { defaultEmail, isOpen, onClose, publishedLink, id, shareable, activeProfile } = props;

    const [email, setEmail] = useState(defaultEmail);
    const [errors, setErrors] = useState({});
    const [link, setLink] = useState(publishedLink);
    const customerContent = activeProfile.customerContent ?? _t.crud.itinerary.shareText();
    const companyContent = activeProfile.companyContent ?? _t.crud.itinerary.shareAdminText();
    const [isCompany, setIsCompany] = useState(false);
    const [description, setDescription] = useState(customerContent + publishedLink);
    const sendEmail = useSendEmail();

    useEffect(() => {
        setLink(publishedLink);
        setDescription(customerContent + publishedLink);
    }, [publishedLink]);

    const handleSetEmail = (e) => {
        const value = e.target.value;
        if (value && validateStringWithRegex(value)) setErrors((prev) => ({ ...prev, email: false }));
        setEmail(value);
    };

    const handleCopy = () => {
        navigator.clipboard
            .writeText(link)
            .then(() => {
                toast.success(_t.crud.itinerary.copySuccessfull());
            })
            .catch(() => {
                toast.error(_t.crud.itinerary.copyFailed());
            });
    };

    const handleConfirmSending = () => {
        if (!email || !validateStringWithRegex(email)) {
            toast.error(_t.global.validation.invalidEmail());
            setErrors((prev) => ({ ...prev, email: true }));
            return;
        }
        sendEmail.mutate({
            recipient: email,
            content: description,
            itineraryId: id,
            isCompany,
            link
        });
    };

    const handleIsCompany = (value) => {
        setIsCompany(value);
        const link = publishedLink + (value ? '?admin=true' : '');
        setDescription(`${value ? companyContent : customerContent} ${link}`);
        setLink(link);
    };

    return (
        <Dialog isOpen={isOpen} onClose={onClose} className="h-screen flex items-center justify-center fixed inset-4">
            <Dialog.Panel className="min-w-[300px] w-full max-w-[600px] max-h-[80%] overflow-auto">
                <div className="pr-4 mb-3">
                    <Dialog.CloseButton />
                    <p className="text-2xl">{_t.crud.itinerary.shareItinerary()}</p>
                </div>
                <div className="w-full flex flex-col gap-3">
                    <Input.Full
                        value={email}
                        name="email"
                        type="email"
                        onChange={handleSetEmail}
                        hasError={errors.email}
                        label={_t.crud.itinerary.sendTo()}
                    />
                    {shareable && (
                        <div className="w-full flex gap-1">
                            <div
                                className={cn(
                                    'w-1/2 p-2 text-center font-semibold rounded-lg transition-all',
                                    !isCompany
                                        ? 'bg-primary-600 text-white'
                                        : 'bg-neutral-50 border border-neutral-200 text-neutral-700 cursor-pointer hover:bg-neutral-200'
                                )}
                                onClick={() => handleIsCompany(false)}
                            >
                                {_t.profile.customer()}
                            </div>
                            <div
                                className={cn(
                                    'w-1/2 p-2 text-center font-semibold rounded-lg transition-all',
                                    isCompany
                                        ? 'bg-primary-600 text-white'
                                        : 'bg-neutral-50 border border-neutral-200 text-neutral-700 cursor-pointer hover:bg-neutral-200'
                                )}
                                onClick={() => handleIsCompany(true)}
                            >
                                {_t.profile.company()}
                            </div>
                        </div>
                    )}
                    <div>
                        <span className="text-neutral-700 text-sm">
                            {isCompany ? _t.crud.itinerary.agencyLink() : _t.crud.itinerary.clientLink()}
                        </span>
                        <div className="flex gap-2 w-full">
                            <Input.Full
                                readOnly={true}
                                className="w-full mb-1 sm:mb-0 sm:min-w-96 overflow-hidden"
                                inputClassName={addClasses('cursor-default text-ellipsis')}
                                value={link}
                            />
                            <Button look="primary">
                                <Icon
                                    icon="material-symbols:content-copy-outline-rounded"
                                    width="1.3rem"
                                    height="1.3rem"
                                    onClick={handleCopy}
                                />
                            </Button>
                        </div>
                    </div>
                    <HTMLEditor
                        onEditorChange={(_, editor) => setDescription(editor.getContent())}
                        value={description ?? ''}
                    />
                    <Button look="primary" busy={sendEmail.isLoading} onClick={handleConfirmSending}>
                        <Icon icon="material-symbols:mail-outline" className="mr-2" width="24" height="24" />
                        {_t.crud.itinerary.sendEmail()}
                    </Button>
                </div>
            </Dialog.Panel>
        </Dialog>
    );
}
